import { FC, useMemo } from "react";
import { cx, priceDisplay } from "@jugl-web/utils";
import getSymbolFromCurrency from "currency-symbol-map";
import { TableCell, TableCellProps } from "../TableCell/TableCell";

export type TablePriceCellProps = {
  /** Price in cents, not including discount */
  price: number;
  currency: string;
  discount?: number;
  discountType?: "percent" | "static";
  unit?: string;
} & Pick<TableCellProps, "classNames">;

export const TablePriceCell: FC<TablePriceCellProps> = ({
  price,
  currency,
  discount,
  discountType = "percent",
  unit,
  classNames,
}) => {
  const currencySymbol = getSymbolFromCurrency(currency);
  const finalPrice = useMemo(() => {
    if (!discount) return price;

    if (discountType === "percent") return price - (price * discount) / 100;
    if (discountType === "static") return price - discount;

    return price;
  }, [price, discount, discountType]);

  const formattedPrice = useMemo(
    () => priceDisplay(finalPrice, currency, true, { isString: true }),
    [finalPrice, currency]
  );

  const hasDiscount = typeof discount === "number" && discount > 0;

  const oldPrice = useMemo(() => {
    return hasDiscount
      ? `${currencySymbol} ${priceDisplay(price, currency, true, {
          isString: true,
        })}`
      : null;
  }, [hasDiscount, currencySymbol, price, currency]);

  return (
    <TableCell classNames={classNames}>
      <div className="flex items-center gap-2 font-secondary">
        <span
          className={cx(
            "font-regular truncate text-sm whitespace-nowrap",
            hasDiscount ? "text-tertiary-700" : "text-dark-800"
          )}
        >
          <span className="font-primary">{currencySymbol}</span>{" "}
          {formattedPrice}
          {unit && <span className="text-grey-700"> / {unit}</span>}
        </span>

        {hasDiscount && (
          <span className="text-xs font-medium text-dark-800 line-through">
            {oldPrice}
          </span>
        )}
      </div>
    </TableCell>
  );
};
